'use client';

import { useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { usePanelBack } from './use-panel-back';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import {
  notifyWorkspacePanelState,
  subscribeWorkspacePanelOpen,
} from './panel-events';

export type PanelEdge = 'left' | 'right' | 'top' | 'bottom';

const STORAGE_KEY = 'workspace-panels-v2';

type PanelState = { open: boolean; size: number };

function loadStorage(): Record<string, PanelState> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<string, PanelState>;
  } catch {
    return {};
  }
}

function saveStorage(map: Record<string, PanelState>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / privacy mode
  }
}

/**
 * Track viewport ≥ md (768px). Hook is `false` on SSR / first paint and
 * flips to true once mounted on a desktop-sized viewport — callers should
 * tolerate the brief mobile-first render.
 *
 * Exported so other workspace surfaces (e.g. the BottomPanel's
 * maximize affordance, which only makes sense on desktop) can branch
 * off the same hook without redefining it.
 */
export function useIsMd(): boolean {
  const [isMd, setIsMd] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsMd(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMd;
}

/**
 * Edge panel.
 *
 * Two presentations:
 *
 * - Desktop (≥ md): a glass card slides out from the edge. The closed
 *   state is a hairline pill plus a wide invisible swipe target.
 * - Mobile (< md): a true sheet that takes most of the viewport when open
 *   (90vw for left/right, 80vh for top/bottom) over a translucent
 *   backdrop. The sheet drags physically with the user's finger; release
 *   past 35% commits the gesture, otherwise it snaps back.
 *
 * In both cases the open/size state is persisted in localStorage per id
 * so the workspace remembers the user's preferred layout. External chrome
 * can request a panel open via `openWorkspacePanel(id, { tab })` from
 * `./panel-events`.
 */
export function PanelShell({
  edge,
  id,
  defaultSize = 320,
  minSize = 220,
  maxSize = 560,
  defaultOpen = false,
  ariaLabel,
  desktopHandle = 'pill',
  closedLabel,
  noClosedHandle = false,
  desktopPlacement = 'edge',
  desktopWidth,
  attached = false,
  trapBack = false,
  cardFocusable = false,
  children,
}: {
  edge: PanelEdge;
  id: string;
  defaultSize?: number;
  minSize?: number;
  maxSize?: number;
  defaultOpen?: boolean;
  ariaLabel: string;
  /**
   * Desktop closed-state handle style. `pill` shows a hairline pill (the
   * historical look, ideal for top/bottom). `button` shows a labelled
   * vertical glass tab — used by left/right side panels so they advertise
   * what's behind them ("Library", "Recent").
   */
  desktopHandle?: 'pill' | 'button';
  /** Required when `desktopHandle === 'button'`; shown as the tab label. */
  closedLabel?: string;
  /**
   * Hide the closed-state handle entirely (both desktop and mobile) —
   * the panel can only be opened via `openWorkspacePanel(id)`. Use this
   * for panels that have a dedicated trigger elsewhere in the chrome
   * (e.g. the Notifications bell), so we don't double-stack a pill on
   * top of an existing button.
   */
  noClosedHandle?: boolean;
  /**
   * Desktop layout strategy for the open panel.
   *  - `edge` (default): full-width along the edge, classic dashboard
   *    panel. Slides in from the edge, resizable along the perpendicular
   *    axis. Used by Library / Recent / Marketplace.
   *  - `top-right`: popover-style card anchored to the top-right
   *    corner, sitting below the top chrome FAB row. Only valid for
   *    `edge="top"`. Used by Notifications so it feels like the bell's
   *    own dropdown rather than a full-screen takeover.
   *  - `bottom-left`: popover-style card anchored to the bottom-left
   *    corner, sitting above the bottom chrome FAB row. Only valid for
   *    `edge="bottom"`. Used by the Marketplace/Widgets panel so it
   *    reads as the Activity FAB's menu rather than a full-width drawer.
   *  - `bottom-center`: popover-style card horizontally centred above
   *    the bottom chrome FAB row. Only valid for `edge="bottom"`. Used
   *    by the Zap panel (Activity / Channels / Discover) so the card
   *    feels balanced regardless of which FAB triggered it.
   */
  desktopPlacement?: 'edge' | 'top-right' | 'bottom-left' | 'bottom-center';
  /** Override the desktop card width (px). For `top-right` placement. */
  desktopWidth?: number;
  /**
   * Desktop ATTACHED mode (the rail dock-panel): instead of a floating card
   * with edge insets + rounded corners + shadow, the panel is a flush
   * full-height column hugging the rail, with a hairline divider to the
   * canvas. Matches the signed-off side-panel wireframe ("don't float").
   */
  attached?: boolean;
  /**
   * Route the browser Back button/gesture into this panel: while it's open, Back
   * closes it (and any drill-down inside it, LIFO) instead of navigating away
   * from the workspace. Used by the left rail panel. See `usePanelBack`.
   */
  trapBack?: boolean;
  /**
   * Let script put the keyboard on the open DESKTOP card (`tabIndex -1`: never
   * a Tab stop of its own, never focused by Tab). The notifications card
   * asks for it (top-panel.tsx): opened from a full-screen bar's menu it sits
   * AFTER the app's frame in the document, so a keyboard that stayed on the
   * bar walked into the app on the next Tab and never reached the card (the
   * visual pass of 2026-10-03). Off by default, so every other panel keeps
   * the focus behaviour it had.
   */
  cardFocusable?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  usePanelBack(!!trapBack && open, () => setOpen(false));
  const [size, setSize] = useState<number>(defaultSize);
  const dragStartRef = useRef<{ start: number; size: number } | null>(null);
  const liveSizeRef = useRef<number>(defaultSize);
  const [collapseHint, setCollapseHint] = useState(false);
  const isMd = useIsMd();

  // Resize-to-collapse: dragging the panel narrower than `minSize` lets it
  // keep shrinking toward a floor; releasing past `collapseThreshold`
  // collapses it back to the rail's icon row (closes it). The gap between
  // `minSize` and `collapseThreshold` is a hysteresis dead zone so the
  // collapse cue never flickers right at the boundary.
  const COLLAPSE_HYSTERESIS = 56;
  const collapseThreshold = minSize - COLLAPSE_HYSTERESIS;
  const collapseFloor = Math.max(48, collapseThreshold - 28);

  useEffect(() => {
    const map = loadStorage();
    const saved = map[id];
    if (saved) {
      // Restore the SIZE only. Restoring `open` made a panel left open in
      // some earlier session RESURRECT itself on a later load — with no
      // tab selected, the left panel then fell back to its default view
      // and the retired Workspaces panel appeared "out of the blue".
      // Panels are on-demand chrome now: they open when asked, never on
      // their own.
      setSize(Math.min(maxSize, Math.max(minSize, saved.size)));
    }
  }, [id, minSize, maxSize]);

  useEffect(() => {
    const map = loadStorage();
    map[id] = { open, size };
    saveStorage(map);
  }, [id, open, size]);

  // Esc closes the open DESKTOP panel — the mobile sheet branch has its
  // own identical handler (it needs one per-sheet for the backdrop).
  // Yields to any consumer that already handled the key (a popover that
  // closed itself and preventDefault'ed); typing focus is NOT excluded
  // on purpose: Esc inside the panel's own filter input should still
  // dismiss the panel.
  useEffect(() => {
    if (!open || !isMd) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, isMd]);

  // Broadcast open-state changes so external chrome (FAB triggers that
  // double as close buttons) can mirror the panel's state without
  // owning a ref to this component. Fires on every open/close transition
  // including the initial mount with the hydrated value.
  useEffect(() => {
    notifyWorkspacePanelState(id, open);
  }, [id, open]);

  // External open requests — allows top-right chrome / shortcuts to
  // ask this specific panel to open by id. `toggle: true` flips the
  // current state so the same FAB acts as show + hide. Sub-tab selection
  // is the responsibility of the panel's content (it can subscribe
  // separately).
  useEffect(() => {
    return subscribeWorkspacePanelOpen((detail) => {
      if (detail.id !== id) return;
      // `expandedOnly` requests that the consumer mount its fullscreen
      // overlay WITHOUT the popover behind it (so closing the
      // fullscreen returns the user to a clean canvas instead of a
      // half-hidden popover). The fullscreen variant listens for the
      // same event separately on the panel body.
      if (detail.expandedOnly) return;
      if (detail.close) setOpen(false);
      else if (detail.toggle) setOpen((prev) => !prev);
      else setOpen(true);
    });
  }, [id]);

  const isHorizontal = edge === 'left' || edge === 'right';

  // ---- desktop resize bar handlers ------------------------------------
  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragStartRef.current = {
      start: isHorizontal ? e.clientX : e.clientY,
      size,
    };
    liveSizeRef.current = size;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const start = dragStartRef.current;
    if (!start) return;
    const cur = isHorizontal ? e.clientX : e.clientY;
    const delta = cur - start.start;
    const sign = edge === 'left' || edge === 'top' ? 1 : -1;
    const raw = start.size + sign * delta;
    // Allow shrinking below `minSize` down to a floor so the gesture has a
    // soft "it's about to collapse" feel; clamp the top to `maxSize`.
    const next = Math.min(maxSize, Math.max(collapseFloor, raw));
    liveSizeRef.current = next;
    setSize(next);
    // Hysteresis: arm the collapse below `collapseThreshold`; only disarm
    // once dragged back past `minSize`. Inside the dead zone, keep state.
    setCollapseHint((prev) =>
      raw < collapseThreshold ? true : raw > minSize ? false : prev,
    );
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const preDragSize = dragStartRef.current?.size ?? defaultSize;
    dragStartRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    const finalSize = liveSizeRef.current;
    if (finalSize < collapseThreshold) {
      // Collapse to the rail's icon row. Restore the pre-collapse width so
      // the next open isn't the tiny drag width.
      setSize(Math.max(minSize, preDragSize));
      setOpen(false);
    } else if (finalSize < minSize) {
      // In the dead zone: snap back up to the min instead of leaving the
      // panel uncomfortably narrow.
      setSize(minSize);
    }
    setCollapseHint(false);
  };

  // ---- desktop swipe-to-open (drag from edge) -------------------------
  const SWIPE_OPEN_THRESHOLD = 32;
  const swipeRef = useRef<{ start: number; opened: boolean } | null>(null);

  const onSwipeOpenDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    swipeRef.current = {
      start: isHorizontal ? e.clientX : e.clientY,
      opened: false,
    };
  };
  const onSwipeOpenMove = (e: React.PointerEvent) => {
    const s = swipeRef.current;
    if (!s || s.opened) return;
    const cur = isHorizontal ? e.clientX : e.clientY;
    const delta = cur - s.start;
    const sign = edge === 'left' || edge === 'top' ? 1 : -1;
    if (sign * delta > SWIPE_OPEN_THRESHOLD) {
      s.opened = true;
      setOpen(true);
    }
  };
  const onSwipeOpenUp = (e: React.PointerEvent) => {
    swipeRef.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
  };

  // Desktop open-card slide-in (must live BEFORE any conditional returns —
  // hooks cannot run only on the "open desktop" branch or React throws
  // "Rendered more hooks than during the previous render").
  const openCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMd || !open) return;
    const el = openCardRef.current;
    if (!el) return;
    const isTopRightPopover =
      desktopPlacement === 'top-right' && edge === 'top';
    const isBottomLeftPopover =
      desktopPlacement === 'bottom-left' && edge === 'bottom';
    const isBottomCenterPopover =
      desktopPlacement === 'bottom-center' && edge === 'bottom';
    const isPopover =
      isTopRightPopover || isBottomLeftPopover || isBottomCenterPopover;
    if (isPopover) {
      // Popover-style: short pop-in from near the FAB, not a slide
      // from the screen edge. Combines a small translate with scale and
      // opacity so it reads as "this is the FAB's menu opening".
      // Origin/translate direction mirrors the anchor corner.
      el.style.transformOrigin = isTopRightPopover
        ? 'top right'
        : isBottomLeftPopover
          ? 'bottom left'
          : 'bottom center';
      el.style.transform = isTopRightPopover
        ? 'translate3d(0,-8px,0) scale(0.96)'
        : 'translate3d(0,8px,0) scale(0.96)';
      el.style.opacity = '0';
      el.style.transition = 'none';
      void el.offsetHeight;
      el.style.transition =
        'transform 180ms cubic-bezier(0.4, 0, 0.2, 1), opacity 160ms ease-out';
      el.style.transform = 'translate3d(0,0,0) scale(1)';
      el.style.opacity = '1';
    } else {
      el.style.transform = closedTransform(edge);
      el.style.transition = 'none';
      void el.offsetHeight;
      el.style.transition = 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1)';
      el.style.transform = 'translate3d(0,0,0)';
    }
    // Once the open animation settles, drop the transform so the
    // liquid-glass `backdrop-filter` actually samples the canvas behind
    // it. A lingering composited transform parks the panel on its own GPU
    // layer and the glass reads flat (the rail looks glassy precisely
    // because it has no transform).
    const settleTid = setTimeout(() => {
      const node = openCardRef.current;
      if (node) node.style.transform = 'none';
    }, 280);
    return () => clearTimeout(settleTid);
  }, [isMd, open, edge, desktopPlacement]);

  // =====================================================================
  // MOBILE
  // =====================================================================
  if (!isMd) {
    return (
      <MobileSheet
        edge={edge}
        open={open}
        onOpenChange={setOpen}
        ariaLabel={ariaLabel}
        noClosedHandle={noClosedHandle}
      >
        {children}
      </MobileSheet>
    );
  }

  // =====================================================================
  // DESKTOP
  // =====================================================================
  if (!open) {
    if (noClosedHandle) return null;
    const showButtonHandle =
      desktopHandle === 'button' && isHorizontal && !!closedLabel;
    return (
      <>
        {/*
          Wide invisible swipe target hugging the edge. We intentionally
          do NOT set a `cursor-*-resize` here — there's no visible handle
          on desktop, so showing a resize cursor over an empty corner of
          the canvas was confusing. The pull-to-open gesture still works
          via pointer drag; on desktop the button handle (`Library` /
          `Recent`) below remains the visible affordance for opening.
        */}
        <div
          onPointerDown={onSwipeOpenDown}
          onPointerMove={onSwipeOpenMove}
          onPointerUp={onSwipeOpenUp}
          onPointerCancel={onSwipeOpenUp}
          onDoubleClick={() => setOpen(true)}
          aria-hidden
          style={{ touchAction: 'none' }}
          className={cn(
            'pointer-events-auto absolute z-10',
            edge === 'left' && 'left-0 top-0 bottom-0 w-6',
            edge === 'right' && 'right-0 top-0 bottom-0 w-6',
            edge === 'top' && 'top-0 left-0 right-0 h-6',
            edge === 'bottom' && 'bottom-0 left-0 right-0 h-6',
          )}
        />
        {showButtonHandle ? (
          // Labelled vertical glass tab for side panels — advertises what
          // lives behind the edge ("Library", "Recent"). The label is
          // rotated so it reads top-to-bottom along the edge. Must sit
          // ABOVE the swipe target (z-20 > z-10) so clicks reach the
          // button instead of being eaten by the invisible swipe div.
          //
          // Uses the same `liquid-glass` recipe as the FABs and the
          // search bar — Apple-style frosted bezel + SVG refraction
          // overlay where supported — so the side handles read as the
          // same family of chrome rather than the older "card-on-
          // canvas" outline the previous `glass-surface` class gave.
          //
          // The `data-onboarding-anchor` attribute is derived from the
          // panel `id` so the overlay can lock its `swipe for library`
          // / `swipe for recent` arrows onto this handle — and only
          // this handle, not the wide invisible swipe target, which has
          // no visible chrome to point at.
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={`Open ${ariaLabel}`}
            data-onboarding-anchor={`edge-${id}`}
            className={cn(
              'pointer-events-auto absolute z-20 liquid-glass',
              'flex items-center justify-center px-1.5 py-2 text-[11px] font-medium tracking-wide',
              'text-foreground/70 hover:text-foreground transition-colors',
              edge === 'left' && 'left-1.5 top-1/2 -translate-y-1/2 rounded-r-xl rounded-l-md',
              edge === 'right' && 'right-1.5 top-1/2 -translate-y-1/2 rounded-l-xl rounded-r-md',
            )}
          >
            <span
              className="block whitespace-nowrap"
              style={{
                writingMode: 'vertical-rl',
                transform: edge === 'left' ? 'rotate(180deg)' : undefined,
              }}
            >
              {closedLabel}
            </span>
          </button>
        ) : (
          // Hairline pill — quiet decorative invitation, used by top/bottom
          // panels (and as a fallback when no closedLabel is provided).
          // Same z-20 as the labelled tab so clicks beat the swipe target.
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={`Open ${ariaLabel}`}
            tabIndex={0}
            className={cn(
              'z-20',
              'pointer-events-auto absolute group transition-opacity',
              'opacity-40 hover:opacity-100 focus-visible:opacity-100 outline-none',
              edge === 'left' && 'left-1.5 top-1/2 -translate-y-1/2',
              edge === 'right' && 'right-1.5 top-1/2 -translate-y-1/2',
              edge === 'top' && 'top-1.5 left-1/2 -translate-x-1/2',
              edge === 'bottom' && 'bottom-1.5 left-1/2 -translate-x-1/2',
            )}
          >
            <span
              className={cn(
                'block rounded-full bg-foreground/30 group-hover:bg-foreground/60 transition-colors',
                isHorizontal ? 'w-1 h-14' : 'h-1 w-14',
              )}
            />
          </button>
        )}
      </>
    );
  }

  const isTopRight = desktopPlacement === 'top-right' && edge === 'top';
  const isBottomLeft =
    desktopPlacement === 'bottom-left' && edge === 'bottom';
  const isBottomCenter =
    desktopPlacement === 'bottom-center' && edge === 'bottom';
  const isPopoverPlacement = isTopRight || isBottomLeft || isBottomCenter;

  const positionStyle: React.CSSProperties = (() => {
    if (isTopRight) {
      // Popover-style: sit under the top chrome row (h-10 at top:12 →
      // baseline ~56) anchored to the right gutter. Width is capped by
      // desktopWidth so the card doesn't stretch across the canvas.
      //
      // THE HEIGHT IS THE DEFAULT, NEVER THE REMEMBERED ONE (2026-10-03).
      // This placement has no resize bar, so nobody ever chose a height
      // for it; the only way the stored size changed was the clamp to
      // `maxSize` (85% of the window for Notifications). One visit in a
      // short window therefore wrote a short card into this browser for
      // good: the visual pass found Notifications 334 px tall on a 900 px
      // screen, with 1,420 px of list inside it. Derived here on every
      // render, it also heals the browsers that already stored one.
      return {
        top: 60,
        right: 12,
        width: desktopWidth ?? 420,
        height: Math.min(maxSize, Math.max(minSize, defaultSize)),
      };
    }
    if (isBottomLeft) {
      // Popover-style anchored above the bottom-left FAB cluster. The
      // FAB sits at `bottom-3` (h-14 = 56px), so `bottom: 80` clears it
      // with a little air. We give the card a maxHeight rather than
      // pinning `top` so it grows downward from the FAB based on its
      // own content, not the viewport.
      return {
        bottom: 80,
        left: 12,
        width: desktopWidth ?? 640,
        maxHeight: 'calc(100vh - 160px)',
      };
    }
    if (isBottomCenter) {
      // Horizontally centred and pinned close to the bottom edge —
      // used by the Zap panel. We anchor with `left/right: 0` + auto
      // margins (rather than `transform: translateX(-50%)`) so the
      // slide-in animation can drive `el.style.transform` without us
      // also having to bake centring into every keyframe.
      //
      // Two important details for the popover to feel right:
      //  - `bottom: 16` sits just above the workspace's bottom FAB
      //    row (which lives at `bottom-3` = 12px). Hugging the bottom
      //    edge makes the popover feel like a tray rising from the
      //    Zap FAB rather than a card floating somewhere in the lower
      //    half of the canvas.
      //  - We set a definite `height` (capped at viewport-100px)
      //    rather than only `maxHeight`. Children with `h-full` and
      //    `overflow-y-auto` need a containing block with a definite
      //    height to actually clip their content and scroll —
      //    `max-height` alone gives them an "auto" height and the
      //    inner scroll containers grow to fit, defeating the
      //    overflow.
      const w = desktopWidth ?? 720;
      return {
        bottom: 16,
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: w,
        maxWidth: 'calc(100vw - 32px)',
        height: 'min(720px, calc(100vh - 100px))',
      };
    }
    switch (edge) {
      case 'left':
        // Attached: flush full-height column hugging the rail (no insets).
        return attached
          ? { top: 0, bottom: 0, left: 0, width: size }
          : { top: 12, bottom: 12, left: 12, width: size };
      case 'right':
        return { top: 12, bottom: 12, right: 12, width: size };
      case 'top':
        return { top: 12, left: 100, right: 100, height: size };
      case 'bottom':
        return { bottom: 12, left: 100, right: 100, height: size };
    }
  })();

  const resizeBar = (
    <div
      role="separator"
      aria-orientation={isHorizontal ? 'vertical' : 'horizontal'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className={cn(
        'absolute pointer-events-auto',
        edge === 'left' && 'right-0 top-0 bottom-0 w-1.5 cursor-col-resize',
        edge === 'right' && 'left-0 top-0 bottom-0 w-1.5 cursor-col-resize',
        edge === 'top' && 'left-0 right-0 bottom-0 h-1.5 cursor-row-resize',
        edge === 'bottom' && 'left-0 right-0 top-0 h-1.5 cursor-row-resize',
      )}
    >
      <div
        className={cn(
          'transition-colors',
          isHorizontal ? 'h-full w-0.5 mx-auto' : 'w-full h-0.5 my-auto',
          collapseHint ? 'bg-primary' : 'hover:bg-ring/40',
        )}
      />
    </div>
  );

  return (
    <div
      ref={openCardRef}
      className={cn(
        // z-30 puts the open card above sibling closed-state handles
        // (which sit at z-20 — Library / Recent tabs). Without this,
        // an open notifications popover slid under the Recent button.
        'pointer-events-auto absolute z-30 liquid-glass ws-panel-solid flex flex-col overflow-hidden',
        // Attached (rail dock): a flush full-height column with a hairline
        // divider to the canvas — no float, no rounding, no drop shadow.
        attached
          ? 'rounded-none border-r border-border/60'
          : 'rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.08)]',
        // Chrome (tabs, headers, search inputs) is not text. Marking the
        // whole panel non-selectable keeps drags from highlighting tab
        // labels; specific content surfaces inside (notification rows,
        // chat bodies) opt back in with `.ws-selectable`.
        'select-none',
        // Resize-to-collapse cue: ringed while the drag is in the
        // collapse zone, so "release to collapse" reads clearly.
        collapseHint && 'ring-2 ring-primary/40',
        // Focused by script only (see `cardFocusable`): the card is a
        // region, not a control, so it draws no focus ring of its own.
        cardFocusable && 'outline-none',
      )}
      style={positionStyle}
      role="region"
      aria-label={ariaLabel}
      tabIndex={cardFocusable ? -1 : undefined}
      // An OPEN desktop card says so in the document (2026-10-03). The
      // canvas's full-screen Esc asks for this mark to know a panel owns
      // the key (fullscreen/panel-owns-esc.ts, which also says why it is
      // the card and not the open-state mirror).
      data-ws-panel-open={id}
    >
      {/*
        Top-right X close. Attached (rail dock) panels render their OWN close
        button inside their header row (so it never collides with per-view
        action buttons), so we suppress this floating one for them.
      */}
      {!attached && (
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={`Close ${ariaLabel}`}
          className={cn(
            'absolute z-20 top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-full',
            'text-muted-foreground hover:text-foreground hover:bg-accent transition-colors',
          )}
        >
          <X className="h-4 w-4" />
        </button>
      )}

      <div className="flex-1 min-w-0 min-h-0 overflow-hidden">{children}</div>

      {/* Resize bar disabled for popover-style placements — the user
          expects the card to stay anchored to its trigger size. */}
      {!isPopoverPlacement && resizeBar}
    </div>
  );
}

/* ============================================================== */
/*  Mobile sheet                                                   */
/* ============================================================== */

/**
 * Mobile-only sheet.
 *
 * Always-mounted backdrop and panel; open/close is implemented as a
 * `transform` + `opacity` transition so we can interpolate during a drag
 * and snap on release. There is no resize on mobile; users can't pull
 * the sheet larger than its preset size.
 *
 * Two gesture sources:
 * - When closed: an invisible edge target catches the first ~5px from the
 *   screen edge and converts horizontal/vertical drags into the sheet's
 *   transform. Releasing past 35% commits to open.
 * - When open: the sheet's header strip drags the sheet back toward the
 *   edge. Releasing past 35% commits to close.
 *
 * Haptic feedback fires on drag start (`selection`) and on completed
 * snap to open or closed (`light`).
 */
function MobileSheet({
  edge,
  open,
  onOpenChange,
  ariaLabel,
  noClosedHandle = false,
  children,
}: {
  edge: PanelEdge;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  ariaLabel: string;
  noClosedHandle?: boolean;
  children: React.ReactNode;
}) {
  const t = useT();
  const sheetRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    start: number;
    size: number;
    opening: boolean;
    moved: boolean;
  } | null>(null);

  const isHorizontal = edge === 'left' || edge === 'right';
  // Sign aligns "positive delta" with "moves toward open" for any edge.
  const openSign = edge === 'left' || edge === 'top' ? 1 : -1;

  // THE SHADOW IS THE OPEN SHEET'S, NEVER THE PARKED ONE'S (the visual pass
  // of 2026-10-03: a grey band ran along the top of every phone-width screen
  // in the light theme). The sheet is always mounted and parks just past its
  // edge by `transform`, and its `shadow-2xl` (25 px down, 50 px of blur)
  // was drawn there too, so the closed notifications sheet, parked at y -719
  // to 0, cast its shadow onto the top of the page; the other three edges
  // did the same on their own sides. `parked` is true from the first paint
  // of a closed sheet (so the server's HTML has no shadow either), goes
  // false the moment it opens or a finger starts dragging it, and comes back
  // only after the slide-out has finished, so the shadow never pops off a
  // sheet that is still on screen.
  const [parked, setParked] = useState(!open);
  const parkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const unpark = () => {
    if (parkTimer.current) {
      clearTimeout(parkTimer.current);
      parkTimer.current = null;
    }
    setParked(false);
  };
  const parkAfterSlide = () => {
    if (parkTimer.current) clearTimeout(parkTimer.current);
    parkTimer.current = setTimeout(() => {
      parkTimer.current = null;
      if (!openRef.current && !dragRef.current) setParked(true);
    }, SHEET_PARK_MS);
  };
  useEffect(
    () => () => {
      if (parkTimer.current) clearTimeout(parkTimer.current);
    },
    [],
  );

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  // Snap to the desired position whenever `open` changes from outside —
  // kept in an effect so the very first paint also positions correctly
  // (the sheet is rendered off-screen via inline style by default).
  useEffect(() => {
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (!sheet || !backdrop) return;
    if (dragRef.current) return; // don't fight an in-progress drag
    sheet.style.transition = 'transform 250ms cubic-bezier(0.4,0,0.2,1)';
    backdrop.style.transition = 'opacity 250ms cubic-bezier(0.4,0,0.2,1)';
    sheet.style.transform = open ? 'translate3d(0,0,0)' : closedTransform(edge);
    backdrop.style.opacity = open ? '1' : '0';
    backdrop.style.pointerEvents = open ? 'auto' : 'none';
    if (open) unpark();
    else parkAfterSlide();
    // (`unpark` and `parkAfterSlide` touch only refs and a setter, so they
    // are not dependencies.)
  }, [open, edge]);

  const measureSize = () => {
    const sheet = sheetRef.current;
    if (!sheet) return 320;
    return isHorizontal ? sheet.offsetWidth : sheet.offsetHeight;
  };

  const beginDrag = (e: React.PointerEvent, opening: boolean) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    // A sheet under a finger is on its way onto the screen: it has its shadow.
    unpark();
    dragRef.current = {
      start: isHorizontal ? e.clientX : e.clientY,
      size: measureSize(),
      opening,
      moved: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (!drag || !sheet || !backdrop) return;
    const cur = isHorizontal ? e.clientX : e.clientY;
    const rawDelta = cur - drag.start;
    const dim = drag.size || measureSize();
    if (!drag.moved && Math.abs(rawDelta) > 4) {
      drag.moved = true;
      haptic('selection');
    }
    sheet.style.transition = 'none';
    backdrop.style.transition = 'none';
    if (drag.opening) {
      // delta in the open direction → moves sheet onto screen.
      const towardOpen = rawDelta * openSign;
      const clamped = Math.max(0, Math.min(dim, towardOpen));
      const offset = dim - clamped;
      sheet.style.transform = openingTransform(edge, offset);
      backdrop.style.opacity = String(clamped / dim);
      backdrop.style.pointerEvents = 'auto';
    } else {
      // delta opposite the open direction → moves sheet back off screen.
      const awayFromOpen = -rawDelta * openSign;
      const clamped = Math.max(0, Math.min(dim, awayFromOpen));
      sheet.style.transform = openingTransform(edge, clamped);
      backdrop.style.opacity = String(1 - clamped / dim);
    }
  };

  const onPointerEnd = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    dragRef.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (!drag || !sheet || !backdrop) return;

    const cur = isHorizontal ? e.clientX : e.clientY;
    const rawDelta = cur - drag.start;
    const dim = drag.size || measureSize();
    const threshold = dim * 0.35;
    sheet.style.transition = 'transform 200ms cubic-bezier(0.4,0,0.2,1)';
    backdrop.style.transition = 'opacity 200ms cubic-bezier(0.4,0,0.2,1)';

    if (drag.opening) {
      const towardOpen = rawDelta * openSign;
      const willOpen = towardOpen >= threshold;
      if (willOpen) {
        sheet.style.transform = 'translate3d(0,0,0)';
        backdrop.style.opacity = '1';
        backdrop.style.pointerEvents = 'auto';
        haptic('light');
        onOpenChange(true);
      } else {
        sheet.style.transform = closedTransform(edge);
        backdrop.style.opacity = '0';
        backdrop.style.pointerEvents = 'none';
        // An opening drag let go too early: `open` never changed, so the
        // effect above will not run; park it once it has slid back.
        parkAfterSlide();
      }
    } else {
      // Tap-to-close: a pointer-down that never crossed the
      // movement threshold is treated as a tap on the handle / its
      // margin. Closing on tap (in addition to drag-to-close) lets
      // mobile users dismiss the sheet with a single touch on the
      // grip area — no need to discover the swipe gesture.
      if (!drag.moved) {
        sheet.style.transform = closedTransform(edge);
        backdrop.style.opacity = '0';
        backdrop.style.pointerEvents = 'none';
        haptic('light');
        onOpenChange(false);
        return;
      }
      const awayFromOpen = -rawDelta * openSign;
      const willClose = awayFromOpen >= threshold;
      if (willClose) {
        sheet.style.transform = closedTransform(edge);
        backdrop.style.opacity = '0';
        backdrop.style.pointerEvents = 'none';
        haptic('light');
        onOpenChange(false);
      } else {
        sheet.style.transform = 'translate3d(0,0,0)';
        backdrop.style.opacity = '1';
      }
    }
  };

  // Horizontal panels keep their fixed comfortable width; top + bottom
  // sheets fill nearly the whole viewport (95dvh) so the drag handle
  // and its tap-margin sit right under the screen edge — there used to
  // be ~80px of empty backdrop between the handle and the top of the
  // viewport which felt like wasted real-estate.
  const sheetSizeClass = isHorizontal
    ? 'w-[88vw] max-w-[420px] h-dvh'
    : 'h-[95dvh] w-screen';

  const sheetPosClass = (() => {
    switch (edge) {
      case 'left': return 'left-0 top-0';
      case 'right': return 'right-0 top-0';
      case 'top': return 'top-0 left-0';
      case 'bottom': return 'bottom-0 left-0';
    }
  })();

  const sheetRadiusClass = (() => {
    switch (edge) {
      case 'left': return 'rounded-r-2xl';
      case 'right': return 'rounded-l-2xl';
      case 'top': return 'rounded-b-2xl';
      case 'bottom': return 'rounded-t-2xl';
    }
  })();

  return (
    <>
      {/* Backdrop — always mounted; opacity + pointer events controlled
          imperatively so drag interpolation is instant. */}
      <div
        ref={backdropRef}
        onClick={() => onOpenChange(false)}
        aria-hidden
        className="fixed inset-0 z-[140] bg-black/40 backdrop-blur-sm"
        style={{ opacity: 0, pointerEvents: 'none' }}
      />

      {/* Close X — SCREEN-fixed at the corner OPPOSITE the sheet edge, with a
          margin off the screen edge. A sibling of the sheet (not a child) so
          it doesn't ride the sheet's drag transform, and so the sheet header
          itself carries no X. Solid puck for legibility over the backdrop. */}
      {open && isHorizontal && (
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label={`Close ${ariaLabel}`}
          className={cn(
            'pointer-events-auto fixed top-3 z-[151] inline-flex h-9 w-9 items-center justify-center rounded-full bg-background text-foreground shadow-lg ring-1 ring-border transition-colors hover:bg-accent',
            edge === 'left' ? 'right-3' : 'left-3',
          )}
        >
          <X className="h-4 w-4" />
        </button>
      )}

      <aside
        ref={sheetRef}
        role="dialog"
        aria-modal={open ? 'true' : undefined}
        aria-label={ariaLabel}
        className={cn(
          // Note: NOT `overflow-hidden` — the outside drag grip child
          // intentionally extends beyond the sheet's content edge.
          // `ws-panel-solid` = fully opaque (no canvas bleed through the sheet).
          'pointer-events-auto fixed z-[150] liquid-glass ws-panel-solid flex flex-col',
          parked ? 'shadow-none' : 'shadow-2xl',
          // Mirror desktop: chrome is non-selectable so swipe drags
          // never accidentally highlight tabs / labels. Scrollable
          // content surfaces opt back in via `.ws-selectable`.
          'select-none',
          sheetSizeClass,
          sheetPosClass,
          sheetRadiusClass,
        )}
        data-parked={parked ? 'true' : undefined}
        style={{ transform: closedTransform(edge), willChange: 'transform' }}
      >
        {/*
          Body — clips its own children so scroll lives inside the
          sheet, while the sibling grip below is free to overflow.
        */}
        <div
          className={cn(
            'flex-1 min-h-0 min-w-0 overflow-hidden',
            sheetRadiusClass,
          )}
        >
          {children}
        </div>

        {/*
          OUTSIDE drag handle — the visible pill grip that doubles as
          the close-gesture source. Lives as a child of the sheet so
          it transforms in lockstep during the drag, but is absolutely
          positioned just past the sheet's content-facing edge.

          Critically gated on `open`: the sheet itself is always
          mounted (so the open/close transform can animate) and slides
          offscreen via `translate3d` while closed. Since the grip is
          anchored at the sheet's content-facing edge, a closed sheet
          parks the grip exactly on the screen edge — which manifested
          as four orphan pills floating at the viewport boundaries.
          Hiding it while closed makes the grip purely a close-affordance.
        */}
        {open && (
          /*
            Handle + margin tap-shield.
            The visible pill sits at the very edge of the sheet, but
            the wrapper itself spans the entire gap between the sheet
            and the viewport edge so the user can tap anywhere in that
            margin to close. The wrapper does NOT call
            `stopPropagation` — it captures the gesture only when a
            pointerdown actually lands inside it; the backdrop
            (z-[140]) handles taps further down the viewport.
            Top/bottom sheets get a 5dvh shield (matches the 95dvh
            sheet height); horizontal sheets get a comfortable 56px.
          */
          <div
            role="separator"
            aria-orientation={isHorizontal ? 'vertical' : 'horizontal'}
            aria-label={t('workspace.panels.dragOrTapToClose')}
            onPointerDown={(e) => beginDrag(e, false)}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerEnd}
            onPointerCancel={onPointerEnd}
            style={{ touchAction: 'none' }}
            className={cn(
              'absolute z-[1] flex items-center justify-center',
              'cursor-grab active:cursor-grabbing',
              edge === 'top' && 'left-0 right-0 top-full h-[5dvh] min-h-12',
              edge === 'bottom' && 'left-0 right-0 bottom-full h-[5dvh] min-h-12',
              edge === 'left' && 'top-0 bottom-0 left-full w-14',
              edge === 'right' && 'top-0 bottom-0 right-full w-14',
            )}
          >
            <span
              className={cn(
                'block rounded-full bg-foreground/55 shadow-md',
                isHorizontal ? 'h-14 w-1.5' : 'h-1.5 w-14 mt-4',
              )}
            />
          </div>
        )}
      </aside>

      {/* Edge swipe target + visible mobile handle. Only mounted while the
          sheet is closed so we don't intercept gestures meant for the open
          sheet. The handle is a thin hairline pill so users see something
          to grab even when not familiar with the swipe affordance.
          When `noClosedHandle` is set, neither the swipe target nor the
          pill is rendered — the panel must be opened via its dedicated
          chrome trigger (e.g. the Notifications bell). */}
      {!open && !noClosedHandle && (
        <>
          <div
            onPointerDown={(e) => beginDrag(e, true)}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerEnd}
            onPointerCancel={onPointerEnd}
            aria-hidden
            style={{ touchAction: 'none' }}
            className={cn(
              'pointer-events-auto fixed z-[100]',
              edge === 'left' && 'left-0 top-0 bottom-0 w-5',
              edge === 'right' && 'right-0 top-0 bottom-0 w-5',
              edge === 'top' && 'top-0 left-0 right-0 h-5',
              edge === 'bottom' && 'bottom-0 left-0 right-0 h-5',
            )}
          />
          <button
            type="button"
            onClick={() => onOpenChange(true)}
            aria-label={`Open ${ariaLabel}`}
            // Onboarding overlay anchor — `edge-left` / `edge-right`
            // captions pin to this hairline pill on mobile. Without
            // the attribute the overlay's `swipe → library` /
            // `← swipe recent` annotations had nothing to anchor to
            // (the desktop labelled-button tab isn't rendered here),
            // so they silently dropped out of the mobile tour.
            data-onboarding-anchor={`edge-${edge}`}
            className={cn(
              'pointer-events-auto fixed z-[101] group transition-opacity',
              'opacity-50 hover:opacity-100 focus-visible:opacity-100 outline-none',
              edge === 'left' && 'left-1.5 top-1/2 -translate-y-1/2',
              edge === 'right' && 'right-1.5 top-1/2 -translate-y-1/2',
              edge === 'top' && 'top-1.5 left-1/2 -translate-x-1/2',
              edge === 'bottom' && 'bottom-1.5 left-1/2 -translate-x-1/2',
            )}
          >
            <span
              className={cn(
                'block rounded-full bg-foreground/40 group-hover:bg-foreground/70 transition-colors',
                isHorizontal ? 'w-1 h-14' : 'h-1 w-14',
              )}
            />
          </button>
        </>
      )}
    </>
  );
}

/** The slide-out is 250 ms (200 after a drag); the shadow goes once it is over. */
const SHEET_PARK_MS = 280;

function closedTransform(edge: PanelEdge): string {
  switch (edge) {
    case 'left': return 'translate3d(-100%, 0, 0)';
    case 'right': return 'translate3d(100%, 0, 0)';
    case 'top': return 'translate3d(0, -100%, 0)';
    case 'bottom': return 'translate3d(0, 100%, 0)';
  }
}

/**
 * Position the sheet at `offsetPx` away from its open position, in the
 * direction of the edge. `offsetPx === 0` is fully open;
 * `offsetPx === sheet size` is fully closed.
 */
function openingTransform(edge: PanelEdge, offsetPx: number): string {
  switch (edge) {
    case 'left': return `translate3d(-${offsetPx}px, 0, 0)`;
    case 'right': return `translate3d(${offsetPx}px, 0, 0)`;
    case 'top': return `translate3d(0, -${offsetPx}px, 0)`;
    case 'bottom': return `translate3d(0, ${offsetPx}px, 0)`;
  }
}

